import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { takeUntil } from 'rxjs';

import { BaseComponent } from '../../../../core/components/base/base.component';
import {
  ReferralAcknowledgement,
  ReferralRecipientOption,
  ShareableSourceType,
  describeReferralFailure,
} from '../../../../core/models/referral.model';
import { FeedbackService } from '../../../../core/services/feedback.service';
import { ReferralService } from '../../../../core/services/referral.service';

export interface ShareEntryDialogData {
  patientId: string;
  patientName: string;
  /** What is being shared, in the words the timeline shows. */
  entryTitle: string;
  sourceType: ShareableSourceType;
  sourceId: string;
  /** Links this entry has already reached, so they are shown as done rather than offered again. */
  sharedWithLinkIds?: string[];
}

export interface ShareEntryDialogResult {
  shared?: boolean;
  /** How many links it went to, for the toast and the counter. */
  links?: number;
}

/**
 * Sharing ONE entry of the patient file with referral links (tasks/referrals-platform-architecture.md §21).
 *
 * Only ACTIVE links are offered: an invitation nobody has accepted cannot receive anything. A link that already has this
 * entry is listed as having it, not offered again, because sharing it twice would mean nothing.
 *
 * Links that may receive the patient FILE (§25) take entries freely. A clinician outside MedicAI (a letter link) takes
 * them within the document allowance of the referral letter already sent for this patient (owner 2026-09-29): each
 * entry is one of that referral's documents, and the server refuses one with no letter sent or no documents left.
 *
 * The acknowledgement is the same one a referral asks for, and for the same reason: a link with no referral of chosen
 * entries for this patient gets one, and what is shared cannot be unshared (D1).
 */
@Component({
  selector: 'app-share-entry-dialog',
  templateUrl: './share-entry-dialog.component.html',
  styleUrls: ['./share-entry-dialog.component.scss'],
})
export class ShareEntryDialogComponent extends BaseComponent implements OnInit {
  options: ReferralRecipientOption[] = [];
  acknowledgement: ReferralAcknowledgement | null = null;
  chosen = new Set<string>();
  acknowledged = false;
  isLoading = true;
  isSaving = false;
  loadError: string | null = null;
  actionError: string | null = null;
  submitAttempted = false;
  /** Links this practice has that this server cannot send to: an empty list is then not an empty network. */
  switchedOffLinks = 0;

  constructor(
    private referralService: ReferralService,
    private dialogRef: MatDialogRef<ShareEntryDialogComponent, ShareEntryDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: ShareEntryDialogData,
    fs: FeedbackService,
    md: MatDialog,
  ) {
    super(fs, md);
  }

  ngOnInit(): void {
    this.referralService.listRecipients().pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ options, acknowledgement, switchedOff }) => {
        this.switchedOffLinks = switchedOff;
        this.options = options.filter((o) => o.status === 'active' && ((o.shareModes ?? []).includes('items') || this.isLetterLink(o)));
        this.acknowledgement = acknowledgement;
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        this.loadError = describeReferralFailure(error, 'Your referral links could not be loaded.');
      },
    });
  }

  /** A clinician outside MedicAI: referral letters only, plus entries within each referral's document allowance. */
  isLetterLink(option: ReferralRecipientOption): boolean {
    const modes = option.shareModes ?? [];
    return modes.includes('summary') && !modes.includes('items');
  }

  alreadyShared(option: ReferralRecipientOption): boolean {
    return (this.data.sharedWithLinkIds || []).includes(option.linkId);
  }

  get offered(): ReferralRecipientOption[] {
    return this.options.filter((o) => !this.alreadyShared(o));
  }

  get alreadyHave(): ReferralRecipientOption[] {
    return this.options.filter((o) => this.alreadyShared(o));
  }

  toggle(linkId: string, checked: boolean): void {
    if (checked) this.chosen.add(linkId);
    else this.chosen.delete(linkId);
  }

  get problems(): string[] {
    const problems: string[] = [];
    if (!this.chosen.size) problems.push('Choose at least one clinician or practice.');
    if (!this.acknowledged) problems.push('Confirm that shared records cannot be unshared.');
    return problems;
  }

  describe(option: ReferralRecipientOption): string {
    return [option.speciality, option.practiceName].filter(Boolean).join(' · ');
  }

  share(): void {
    this.submitAttempted = true;
    if (this.problems.length || this.isSaving || !this.acknowledgement) return;
    this.isSaving = true;
    this.actionError = null;
    const linkIds = [...this.chosen];
    this.referralService.shareEntryWithLinks(this.data.patientId, this.data.sourceType, this.data.sourceId, linkIds, this.acknowledgement.version)
      .pipe(takeUntil(this.destroy$)).subscribe({
        next: (result) => {
          this.isSaving = false;
          // A link that already receives the whole file needed nothing: saying "shared" would claim an action nobody took.
          const covered = (result.shared || []).filter((s) => s.alreadyCovered).length;
          const added = (result.shared || []).length - covered;
          this.showToastMessage(
            added === 0 ? 'They already receive this patient\'s whole file, so this entry reaches them anyway.'
              : covered === 0 ? (added === 1 ? 'Shared. It reaches them shortly.' : `Shared with ${added} links. It reaches them shortly.`)
                : `Shared with ${added}. The ${covered === 1 ? 'other already receives' : 'others already receive'} this patient's whole file.`);
          this.dialogRef.close({ shared: true, links: added });
        },
        error: (error) => {
          this.isSaving = false;
          this.actionError = describeReferralFailure(error, 'This entry could not be shared. Please try again.');
        },
      });
  }

  close(): void {
    this.dialogRef.close({});
  }
}
